import React, { Component } from 'react';
import PropTypes from 'prop-types';
import plyr from 'plyr';

import difference from 'lodash/difference';
import pick from 'lodash/pick';

import defaultProps from './defaultProps';
/*******
The MIT License (MIT)

Copyright (c) 2016 Jose Miguel Bejarano <yo@josemiguel.org> (josemiguel.org)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*******/
class Plyr extends Component {
  constructor(props) {
    super(props);
    this.player = null;
    this.elementRef = null; //new React.createRef();
    this._isMounted = false;

    this.restProps = difference(Object.keys(this.props), Object.keys(Plyr.defaultProps));

    this.state = {
      muted: props.muted,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.muted !== state.muted) {
      return {
        muted: props.muted,
      };
    }
    return null;
  }

  getType = () => this.player && this.player.source && this.player.source.type;
  play = () => this.player && this.player.play();
  pause = () => this.player && this.player.pause();
  togglePlay = () => this.player && this.player.togglePlay();
  stop = () => this.player && this.player.stop();
  restart = () => this.player && this.player.restart();
  rewind = (time) => this.player && this.player.rewind(time);
  forward = (time) => this.player && this.player.forward(time);
  getCurrentTime = () => this.player && this.player.currentTime;
  setCurrentTime = (currentTime) => (this.player.currentTime = currentTime);
  getDuration = () => this.player && this.player.duration;
  getVolume = () => this.player && this.player.volume;
  isMuted = () => this.player && this.player.muted;
  isPaused = () => this.player && this.player.paused;
  toggleMute = () => this.player && this.player.toggleControls(this.player.muted);
  setMuted = (muted = true) => (this.player.muted = muted);
  increaseVolume = (step) => this.player && this.player.increaseVolume(step);
  decreaseVolume = (step) => this.player && this.player.decreaseVolume(step);
  setVolume = (amount) => (this.player.volume = amount);
  enterFullscreen = () => this.player && this.player.fullscreen.enter();
  exitFullscreen = () => this.player && this.player.fullscreen.exit();
  toggleFullscreen = () => this.player && this.player.fullscreen.toggle();

  componentDidMount() {
    this._isMounted = true;

    // Use requestAnimationFrame to ensure DOM is ready and check if still mounted
    requestAnimationFrame(() => {
      if (!this._isMounted) {
        return; // Component already unmounting/unmounted
      }

      const defaultOptions = Object.keys(defaultProps).reduce(
        (acc, current) => ({
          ...acc,
          [current]: this.props[current],
        }),
        {},
      );

      const options = {
        ...defaultOptions,
        muted: this.state.muted,
      };

      const node = this.elementRef;

      // Verify node exists and is still in the DOM
      if (!node || !document.contains(node) || !this._isMounted) {
        return;
      }

      try {
        // Create player - this may throw if media element is null
        this.player = new plyr(node, options);

        // Verify player was created successfully
        // Note: For Vimeo/YouTube, media might be null initially and created asynchronously
        // So we check if player exists, not media
        if (!this.player) {
          return;
        }

        if (this.player && this._isMounted) {
          this.player.on('ready', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onReady) {
              try {
                this.props.onReady(this.player);

                // TODO: workaround for autoplay in Youtube
                // https://github.com/sampotts/plyr/issues/1185
                if (this.props.autoplay && this.player && this.player.media) {
                  this.player.play();
                }
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('play', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onPlay) {
              try {
                this.props.onPlay();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('pause', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onPause) {
              try {
                this.props.onPause();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('ended', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onEnd) {
              try {
                this.props.onEnd();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('loadeddata', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onLoadedData) {
              try {
                this.props.onLoadedData();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('seeked', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onSeeked) {
              try {
                const time = this.getCurrentTime();
                this.props.onSeeked(time);
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('ratechange', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onRateChange) {
              try {
                const { speed } = this.player;
                this.props.onRateChange(speed);
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('timeupdate', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onTimeUpdate) {
              try {
                const time = this.getCurrentTime();
                this.props.onTimeUpdate(time);
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('enterfullscreen', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onEnterFullscreen
            ) {
              try {
                this.props.onEnterFullscreen();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('exitfullscreen', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onExitFullscreen
            ) {
              try {
                this.props.onExitFullscreen();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('volumechange', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onVolumeChange) {
              try {
                const { muted, volume } = this.player;
                this.props.onVolumeChange({ muted, volume });
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('languagechange', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onLanguageChange
            ) {
              try {
                const { language } = this.player;
                this.props.onLanguageChange(language);
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('controlshidden', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onControlsHidden
            ) {
              try {
                this.props.onControlsHidden();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('controlsshown', () => {
            if (this._isMounted && this.player && this.player.media && this.props.onControlsShown) {
              try {
                this.props.onControlsShown();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('captionsenabled', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onCaptionsEnabled
            ) {
              try {
                this.props.onCaptionsEnabled();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });

          this.player.on('captionsdisabled', () => {
            if (
              this._isMounted &&
              this.player &&
              this.player.media &&
              this.props.onCaptionsDisabled
            ) {
              try {
                this.props.onCaptionsDisabled();
              } catch (error) {
                // Silently ignore - component may be unmounting
              }
            }
          });
        }
      } catch (error) {
        // Handle initialization errors gracefully
        // This catches "Cannot set properties of null" errors when media isn't ready yet
        // or when DOM node is removed during initialization
        this.player = null;
        // Don't log errors if component is unmounting - this is expected
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (!this._isMounted || !this.player || !this.player.media) {
      return;
    }

    try {
      // Verify player media still exists before updating
      if (prevProps.muted !== this.props.muted && this.player.media) {
        this.player.muted = this.props.muted;
      }

      if (prevProps.videoId !== this.props.videoId && this.player.media) {
        this.props.videoId && this.updateVideoSource(this.props.videoId, this.props.provider);
      }
    } catch (error) {
      // Silently ignore errors during unmount or when media is null
      // This prevents "Cannot set properties of null" crashes
    }
  }

  componentWillUnmount() {
    // Set unmounted flag FIRST to prevent any async operations from continuing
    this._isMounted = false;

    if (this.player) {
      try {
        // Check if player media and DOM node still exist before destroying
        // This prevents errors when DOM is already removed
        const nodeStillExists = this.elementRef && document.contains(this.elementRef);
        if (this.player.media && nodeStillExists) {
          this.player.destroy();
        }
      } catch (error) {
        // Silently ignore errors during cleanup - they're expected when DOM is removed quickly
        // This prevents "Cannot read properties of null" crashes
      } finally {
        // Always null out references to prevent any further access
        this.player = null;
        this.elementRef = null;
      }
    } else {
      // Clean up ref even if player wasn't created yet
      this.elementRef = null;
    }
  }

  updateVideoSource = (videoId, provider) => {
    if (!this._isMounted || !this.player || !this.player.media) {
      return;
    }

    try {
      // Only update if player and media still exist
      if (this.player.media) {
        this.player.source = {
          type: 'video',
          sources: [
            {
              src: videoId,
              provider,
            },
          ],
        };
      }
    } catch (error) {
      // Silently ignore - media may have been destroyed during unmount
    }
  };

  // For video support for plyr supported videos using videoId (Youtube and Vimeo for now).
  renderPlayerWithVideoId = () => {
    return (
      <div
        data-plyr-provider={this.props.provider}
        data-plyr-embed-id={this.props.videoId}
        ref={(e) => {
          // Always set ref - React will call this with null during unmount
          // We guard against using it after unmount in componentDidMount and componentWillUnmount
          this.elementRef = e;
        }}
        {...pick(this.props, this.restProps)}
      />
    );
  };

  // For video support for source defined as link to those video files.
  renderPlayerWithSRC = () => {
    const { sources, url, preload, poster, tracks, ...rest } = this.props;

    const captionsMap = tracks.map((source, index) => {
      const {
        key = index,
        kind = 'captions',
        label,
        src,
        srclang,
        default: def,
        ...attributes
      } = source;

      return (
        <track
          key={key}
          kind={kind}
          label={label}
          src={src}
          srclang={srclang}
          default={def}
          {...attributes}
          ref={this.elementRef}
        />
      );
    });

    if (sources && Array.isArray(sources) && sources.length) {
      return (
        <video
          preload={preload}
          poster={poster}
          ref={(e) => {
            // Only set ref if component is still mounted
            if (this._isMounted !== false) {
              this.elementRef = e;
            }
          }}
          {...pick(rest, this.restProps)}
        >
          {sources.map((source, index) => (
            <source
              key={index}
              src={source.src}
              type={source.type}
              size={source.size && source.size}
            />
          ))}
          {captionsMap}
        </video>
      );
    }

    return (
      <video
        src={url}
        preload={preload}
        poster={poster}
        ref={(e) => {
          // Only set ref if component is still mounted
          if (this._isMounted !== false) {
            this.elementRef = e;
          }
        }}
        {...pick(rest, this.restProps)}
      >
        {captionsMap}
      </video>
    );
  };

  render() {
    // if (this.props.provider === 'audio') {
    //   return this.renderAudioPlayer();
    // } else if (this.props.provider === 'html5') {
    //   return this.renderPlayerWithSRC();
    // }

    return this.renderPlayerWithVideoId();
  }
}

// Specifies the default values for props:
Plyr.defaultProps = {
  provider: 'vimeo',
  videoId: null,
  url: null,
  tracks: [],
  sources: [],

  onReady: () => {},
  onPlay: () => {},
  onPause: () => {},
  onEnd: () => {},
  onLoadedData: () => {},
  onSeeked: () => {},
  onRateChange: () => {},
  onTimeUpdate: () => {},
  onEnterFullscreen: () => {},
  onExitFullscreen: () => {},
  onVolumeChange: () => {},
  onLanguageChange: () => {},
  onControlsHidden: () => {},
  onControlsShown: () => {},
  onCaptionsEnabled: () => {},
  onCaptionsDisabled: () => {},

  ...defaultProps,
};

Plyr.propTypes = {
  provider: PropTypes.oneOf(['youtube', 'vimeo', 'html5', 'audio']),
  videoId: PropTypes.string,
  url: PropTypes.string,

  onReady: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnd: PropTypes.func,
  onLoadedData: PropTypes.func,
  onSeeked: PropTypes.func,
  onRateChange: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onEnterFullscreen: PropTypes.func,
  onExitFullscreen: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onLanguageChange: PropTypes.func,
  onControlsHidden: PropTypes.func,
  onControlsShown: PropTypes.func,
  onCaptionsEnabled: PropTypes.func,
  onCaptionsDisabled: PropTypes.func,

  // plyr props
  enabled: PropTypes.bool,
  title: PropTypes.string,
  debug: PropTypes.bool,
  autoplay: PropTypes.bool,
  autopause: PropTypes.bool,
  seekTime: PropTypes.number,
  volume: PropTypes.number,
  muted: PropTypes.bool,
  duration: PropTypes.number,
  displayDuration: PropTypes.bool,
  invertTime: PropTypes.bool,
  toggleInvert: PropTypes.bool,
  ratio: PropTypes.string,
  clickToPlay: PropTypes.bool,
  hideControls: PropTypes.bool,
  resetOnEnd: PropTypes.bool,
  disableContextMenu: PropTypes.bool,
  loadSprite: PropTypes.bool,
  iconPrefix: PropTypes.string,
  iconUrl: PropTypes.string,
  blankVideo: PropTypes.string,
  quality: PropTypes.shape({
    default: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }),
  loop: PropTypes.shape({
    active: PropTypes.bool,
  }),
  speed: PropTypes.shape({
    selected: PropTypes.number,
    options: PropTypes.arrayOf(PropTypes.number),
  }),
  keyboard: PropTypes.shape({
    focused: PropTypes.bool,
    global: PropTypes.bool,
  }),
  tooltips: PropTypes.shape({
    controls: PropTypes.bool,
    seek: PropTypes.bool,
  }),
  captions: PropTypes.shape({
    active: PropTypes.bool,
    language: PropTypes.string,
    update: PropTypes.bool,
  }),
  fullscreen: PropTypes.shape({
    enabled: PropTypes.bool,
    fallback: PropTypes.bool,
    iosNative: PropTypes.bool,
  }),
  storage: PropTypes.shape({
    enabled: PropTypes.bool,
    key: PropTypes.string,
  }),
  controls: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOf([
        'play-large',
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'captions',
        'settings',
        'pip',
        'airplay',
        'fullscreen',
      ]),
    ),
    PropTypes.func,
    PropTypes.object,
    PropTypes.bool,
  ]),
  settings: PropTypes.arrayOf(PropTypes.oneOf(['captions', 'quality', 'speed'])),
  poster: PropTypes.string,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      size: PropTypes.number,
    }),
  ),
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string,
      label: PropTypes.string,
      src: PropTypes.string.isRequired,
      srclang: PropTypes.string,
      default: PropTypes.bool,
      key: PropTypes.any,
    }),
  ),
};

export default Plyr;
